import React, { useState, useRef, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import { PinInputWrapper, PinInputDigit, ShowPin } from './PinCode.styles';
import { AllieText } from '../../../components';
import { AllieEyeCloseIcon, AllieEyeOpenIcon } from '../../../styling/icons';

const BACKSPACE_KEY = 8;
const LEFT_ARROW_KEY = 37;
const UP_ARROW_KEY = 38;
const RIGHT_ARROW_KEY = 39;
const DOWN_ARROW_KEY = 40;
const E_KEY = 69;

interface PinCodeProps {
  fields: number;
  hideLabel: string;
  isValid?: boolean;
  onChange?: (value: string) => void;
  showLabel: string;
}

export const PinCode: React.FC<PinCodeProps> = ({
  fields,
  hideLabel,
  isValid,
  onChange,
  showLabel,
}) => {
  const [firstRender, setFirstRender] = useState(true);
  const [input, setInput] = useState<string[]>([]);
  const [value, setValue] = useState<string>('');
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [showPin, setShowPin] = useState(false);

  useEffect(() => {
    const newInput = [];
    for (let i = 0; i < fields; i += 1) {
      if (i < 32) {
        const inputValue = value[i] || '';
        newInput.push(inputValue);
      }
    }
    setInput(newInput);
  }, [fields, value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetId = Number(e.target.dataset.id);
    if (!isNaN(targetId)) {
      let newValue = e.target.value;
      newValue = newValue.replace(/[^\d]/g, '');
      let fullValue = newValue;
      if (newValue !== '') {
        const newInput = [...input];
        newInput[targetId] = newValue;
        newInput.forEach((char, i) => {
          if (inputsRef.current[i]) {
            inputsRef.current[i].value = char;
          }
        });
        const newTarget = inputsRef.current[targetId < newInput.length ? targetId + 1 : targetId];
        if (newTarget) {
          newTarget.focus();
        }
        fullValue = newInput.join('');
        setInput(newInput);
      }

      if (onChange && fullValue) {
        onChange(fullValue);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = Number(e.currentTarget.dataset.id);
    const nextTarget = inputsRef.current[target + 1];
    const prevTarget = inputsRef.current[target - 1];

    let newValue;

    switch (e.keyCode) {
      case BACKSPACE_KEY:
        e.preventDefault();
        inputsRef.current[target].value = '';
        newValue = [...input];
        newValue[target] = '';
        newValue = newValue.join('');
        setValue(newValue);

        if (inputsRef.current[target].value === '') {
          if (prevTarget) {
            prevTarget.focus();
          }
        }

        if (onChange) {
          onChange(newValue);
        }
        break;

      case LEFT_ARROW_KEY:
        e.preventDefault();
        if (prevTarget) {
          prevTarget.focus();
        }
        break;

      case RIGHT_ARROW_KEY:
        e.preventDefault();
        if (nextTarget) {
          nextTarget.focus();
        }
        break;

      case UP_ARROW_KEY:
        e.preventDefault();
        break;

      case DOWN_ARROW_KEY:
        e.preventDefault();
        break;

      case E_KEY:
        if (e.currentTarget.type === 'number') {
          e.preventDefault();
        }
        break;

      default:
        break;
    }
    if (!Number.isInteger(Number(e.key))) {
      e.preventDefault();
    }
  };

  const getDisplayValue = (val: string) => {
    return showPin ? val : val.replace(/./g, '∗');
  };

  const handleShowPin = () => {
    setShowPin(!showPin);
  };

  return (
    <>
      <PinInputWrapper>
        {input.map((inputValue, i) => {
          return (
            <PinInputDigit
              ref={(ref) => {
                if (ref) {
                  inputsRef.current[i] = ref;
                  if (firstRender) {
                    inputsRef.current[0].focus();
                    setFirstRender(false);
                  }
                }
              }}
              data-id={i}
              value={getDisplayValue(inputValue)}
              key={`input_${i}`}
              type="text"
              inputMode="numeric"
              min={0}
              max={1}
              maxLength={input.length === i + 1 ? 1 : input.length}
              onFocus={(e) => e.target.select()}
              onChange={(e) => handleChange(e)}
              onKeyDown={(e) => handleKeyDown(e)}
              data-valid={isValid}
              pattern="[0-9]*"
            />
          );
        })}
      </PinInputWrapper>
      <ShowPin
        onClick={handleShowPin}
        style={{
          marginTop: 16,
          display: 'flex',
          cursor: 'pointer',
          width: 'fit-content',
        }}
      >
        {showPin ? <AllieEyeOpenIcon /> : <AllieEyeCloseIcon />}
        <AllieText type="body2" weight={600} color="neutral-50">
          {showPin ? hideLabel : showLabel}
        </AllieText>
      </ShowPin>
    </>
  );
};
